import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  Stack,
  SvgIcon,
  TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import {UserAvatar} from '../../../../screens/private/office/professional-caregiver/planner/calendar/shared/user-avatar';
import {translations} from '../../../../i18n/translations';
import {useEntity} from '../../../../contexts/entity/context';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {getLocalizedFieldValue} from '../../../../utils/functions/get-localized-field-value';

export type DailyCareMultiUsersSelectProps = {
  options?: AffiliatedUser[];
  value?: AffiliatedUser[];
  disabled?: boolean;
  label: string;
  onChange: (users: string[]) => void;
};

export function DailyCareMultiUsersSelect(props: DailyCareMultiUsersSelectProps) {
  const {t} = useTranslation();
  const entityContext = useEntity();

  const options: SelectOptionValue[] | undefined = useMemo(() => {
    const users =
      props.options?.map<SelectOptionValue>((item) => ({
        id: item.user.id!,
        name: item.user.name,
        picture: item.user.picture,
        socialResponses: item.affiliation.socialResponses,
        type: 'user',
      })) ?? [];

    const socialResponses = entityContext.state.userEntitySocialResponses.map<SelectOptionValue>(
      (socialResponse) => ({
        id: socialResponse.id!,
        name: t(translations.planner.modal.create.activity.add_users_mark, {
          sr: getLocalizedFieldValue(socialResponse, 'abbreviation'),
          count:
            props.options?.filter((item) =>
              item.affiliation?.socialResponses?.includes(socialResponse.id)
            ).length ?? 0,
        }),
        type: 'social_response',
      })
    );

    return [...socialResponses, ...users];
  }, [props.options, entityContext.state.userEntitySocialResponses, t]);

  const selectedOptions: SelectOptionValue[] | undefined = useMemo(() => {
    return props.value?.map<SelectOptionValue>((user) => ({
      id: user.user.id!,
      name: user.user.name,
      picture: user.user.picture,
      socialResponses: user.affiliation.socialResponses,
      type: 'user',
    }));
  }, [props.value]);

  return (
    <UsersMultiSearchSelectInput
      options={options}
      selectedOptions={selectedOptions}
      disabled={props.disabled}
      label={props.label}
      onChange={(values) => props.onChange(values.map((value) => value.id))}
    />
  );
}

type SelectOptionValue = {
  id: string;
  name: string;
  picture?: string;
  socialResponses?: string[];
  type: 'user' | 'social_response';
};

type UsersMultiSearchSelectInputProps = {
  options?: SelectOptionValue[];
  selectedOptions?: SelectOptionValue[];
  disabled?: boolean;
  label: string;
  onChange?: (values: SelectOptionValue[]) => void;
};

function UsersMultiSearchSelectInput(props: UsersMultiSearchSelectInputProps) {
  const [selectedOptions, setSelectedOptions] = useState<SelectOptionValue[]>(
    props.selectedOptions ?? []
  );
  const {t} = useTranslation();

  useEffect(() => {
    setSelectedOptions((currentSelectedOptions) => [
      // Social-response options are UI-only selections. Keep them when the
      // selected users are synchronized from the parent.
      ...currentSelectedOptions.filter((option) => option.type === 'social_response'),
      ...(props.selectedOptions ?? []),
    ]);
  }, [props.selectedOptions]);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={props.options ?? []}
      groupBy={(option) =>
        t(translations.planner.modal.create.activity.add_users_type[option.type])
      }
      value={selectedOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <Input
          params={params}
          label={props.label}
          displayStartAdornment={selectedOptions.length === 0}
        />
      )}
      loading={props.options === undefined}
      loadingText={t(translations.loading_content.loading)}
      noOptionsText={t(translations.search_select_input.no_options)}
      disabled={props.disabled}
      renderOption={(props, option, state) => (
        <Option key={option.id} htmlProps={props} value={option} selected={state.selected} />
      )}
      onChange={(_, values, reason, details) => {
        // Add / Remove
        if (reason === 'selectOption' || reason === 'removeOption' || reason === 'clear') {
          const clickedValue = details?.option;
          const selectedSocialResponseOptions = values.filter(
            (value) => value.type === 'social_response'
          );
          const selectedUsers = values.filter((value) => value.type === 'user');

          if (clickedValue?.type === 'social_response') {
            if (reason === 'selectOption') {
              const users =
                props.options?.filter((option) => {
                  if (option.type === 'social_response') return false;

                  return option.socialResponses?.some((socialResponse) =>
                    values.some(
                      (selectBySocialResponse) => selectBySocialResponse.id === socialResponse
                    )
                  );
                }) ?? [];

              setSelectedOptions([...selectedSocialResponseOptions, ...users]);
              props.onChange?.(users);
            }
            // removing
            else {
              const users =
                selectedUsers?.filter((user) => {
                  return user.socialResponses?.some(
                    (socialResponse) =>
                      clickedValue.id !== socialResponse &&
                      selectedSocialResponseOptions.some((x) => x.id === socialResponse)
                  );
                }) ?? [];

              setSelectedOptions([...selectedSocialResponseOptions, ...users]);
              props.onChange?.(users);
            }
          } else {
            setSelectedOptions([...selectedSocialResponseOptions, ...selectedUsers]);
            props.onChange?.(selectedUsers);
          }
        }
      }}
      renderTags={(value, getTagProps) => {
        return value.map((option, index) => {
          if (option.type === 'social_response') return null;
          return (
            <Stack {...getTagProps({index})} flexDirection="row" gap="8px">
              <UserAvatar
                size="s"
                disableTooltip
                name={option.name}
                picture={option.picture}
                socialResponses={option.socialResponses}
              />
              <span>{option.name}</span>
            </Stack>
          );
        });
      }}
    />
  );
}

function Option(props: {
  htmlProps: React.HTMLAttributes<HTMLElement>;
  value: SelectOptionValue;
  selected: boolean;
}) {
  return (
    <Stack
      {...props.htmlProps}
      flexDirection="row"
      height="42px"
      padding="0 6px !important"
      gap="8px"
    >
      <Checkbox size="small" checked={props.selected} sx={{width: '12px', height: '12px'}} />
      {props.value.type === 'user' && (
        <UserAvatar
          size="s"
          disableTooltip
          name={props.value.name}
          picture={props.value.picture}
          socialResponses={props.value.socialResponses}
        />
      )}
      <span>{props.value.name}</span>
      <Box sx={{height: '28px', width: '28px', display: 'flex', marginLeft: 'auto'}}>
        {props.selected && (
          <SvgIcon sx={{fontSize: 'small', margin: 'auto', mr: '9px'}}>
            <CheckIcon />
          </SvgIcon>
        )}
      </Box>
    </Stack>
  );
}

function Input(props: {
  params: AutocompleteRenderInputParams;
  label: string;
  displayStartAdornment: boolean;
}) {
  return (
    <TextField
      {...props.params}
      variant="filled"
      label={props.label}
      required
      InputProps={{
        ...props.params.InputProps,
        startAdornment: (
          <>
            <GroupOutlinedIcon />
            {props.params.InputProps.startAdornment}
          </>
        ),
      }}
      sx={{
        '& .MuiInputBase-root': {
          minHeight: '56px',
          lineHeight: '24px',
          padding: '22px 12px 12px 12px',
          alignContent: 'center',
          columnGap: '6px',
          backgroundColor: 'var(--dcm-white-base)',
          boxShadow: '0px 4px 16px 0px rgba(29, 41, 57, 0.1)',
          borderRadius: '5px',
          fontSize: '16px',
          fontWeight: 500,
          ':hover': {
            backgroundColor: 'var(--dcm-white-base)',
            filter: 'none',
          },
        },
        '& .MuiInputBase-input': {
          color: 'var(--dcm-gray)',
        },
        '& .MuiInputAdornment-root': {
          margin: '0 !important',
          height: '100%',
          color: 'var(--dcm-gray)',
        },
        '& .MuiInputLabel-root': {
          color: 'var(--dcm-gray-light)',
          '&.Mui-focused': {
            color: 'var(--dcm-gray-light)',
          },
        },
      }}
    />
  );
}
